import type { BlogArticleJob } from '@/store/slices/blog';

import { ROUTE } from '../../../../routes';
import { pickChip, type WebsiteCardBody, type WebsiteCardIntent, type WebsiteCardSummary } from '../../card-summary';
import type { ReadStatus } from '../creatives/creatives-summary';

/**
 * What the Blog card says, from what its hook read through the blog slice: the brand's articles, its tenant switch
 * (provisioned, on, banned), its Autopilot settings, the open credits notice (the signal Home's Spotlight shows) and
 * how many articles are being written. Pure: copy is an id, mapped to its key in `blog-copy.ts`.
 */

export type BlogCardInput = {
  /**
   * Ready once the articles, the tenant switch, the Autopilot settings and the notices have answered (a failed
   * Autopilot or notices read has answered: the card goes on without it); failed when the articles or tenant read
   * failed.
   */
  status: ReadStatus;
  tenant: { provisioned: boolean; enabled: boolean; banned: boolean };
  articles: Array<{
    id: string;
    status: 'draft' | 'published' | string;
    updatedAt: string;
    publishedAt: string | null;
  }>;
  /** Null when this service build has no Autopilot, or its settings are unread. */
  autopilot: { enabled: boolean; nextRunAt: string | null } | null;
  /** The open credits notice's price, null when none is open. */
  creditsNeeded: number | null;
  /** Articles of this brand being written now. */
  writing: number;
};

export type BlogCopy =
  | { id: 'needs-credits' }
  | { id: 'off' }
  | { id: 'writing'; n: number }
  | { id: 'autopilot-needs-credits' }
  | { id: 'drafts-to-finish'; n: number }
  | { id: 'blog-off' }
  | { id: 'autopilot-next'; at: string }
  | { id: 'last-article'; at: string }
  | { id: 'write-first-article' }
  | { id: 'blog-unavailable' }
  | { id: 'buy-credits' }
  | { id: 'continue-draft' }
  | { id: 'new-article' }
  | { id: 'view-articles' }
  | { id: 'open-blog' };

type Article = BlogCardInput['articles'][number];

/**
 * How many articles are being written, from the blog slice's jobs: running whole-article jobs only, the layout's own
 * rule (`MainLayout` polls the jobs while one runs). A hero, SEO or body pass is not an article being written, and
 * nothing on the hub would ever refresh one read as running.
 */
export const articlesBeingWritten = (jobs: Readonly<Record<string, BlogArticleJob>>): number =>
  Object.values(jobs).filter((job) => job.status === 'running' && job.kind === 'article').length;

/** An ISO stamp as an instant; an unreadable one sorts before every real one. */
const instant = (iso: string): number => {
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? Number.NEGATIVE_INFINITY : ms;
};

/** The draft updated last (the one "Continue draft" opens), or null when nothing is a draft. */
export const latestDraft = (articles: BlogCardInput['articles']): Article | null => {
  let latest: Article | null = null;
  for (const candidate of articles) {
    if (candidate.status !== 'draft') continue;
    if (!latest || instant(candidate.updatedAt) > instant(latest.updatedAt)) latest = candidate;
  }
  return latest;
};

/** When the newest published article went out, or null when none is published. */
const lastPublishedAt = (articles: BlogCardInput['articles']): string | null => {
  let latest: string | null = null;
  for (const candidate of articles) {
    if (candidate.status !== 'published' || !candidate.publishedAt) continue;
    if (latest === null || instant(candidate.publishedAt) > instant(latest)) latest = candidate.publishedAt;
  }
  return latest;
};

const OPEN_BLOG: WebsiteCardIntent<BlogCopy> = { copy: { id: 'open-blog' }, to: ROUTE.blog() };
const VIEW_ARTICLES: WebsiteCardIntent<BlogCopy> = { copy: { id: 'view-articles' }, to: ROUTE.blog() };
const NEW_ARTICLE: WebsiteCardIntent<BlogCopy> = { copy: { id: 'new-article' }, to: ROUTE.blogNew() };
const WRITE_FIRST: WebsiteCardBody<BlogCopy> = { kind: 'get-started', line: { id: 'write-first-article' } };

export const deriveBlogSummary = (input: BlogCardInput): WebsiteCardSummary<BlogCopy> => {
  const { status, tenant, articles, autopilot, creditsNeeded, writing } = input;
  const writingChip = writing > 0 ? { tone: 'working' as const, copy: { id: 'writing' as const, n: writing } } : null;

  if (status === 'loading')
    return { chip: writingChip, body: { kind: 'loading' }, primary: OPEN_BLOG, secondary: null };
  if (status === 'failed') return { chip: writingChip, body: { kind: 'failed' }, primary: OPEN_BLOG, secondary: null };

  // Nothing on the card can be acted on until the blog exists: the Blog page explains why, and the owner's switch
  // there is what provisions it.
  if (!tenant.provisioned || tenant.banned) {
    return {
      chip: writingChip,
      body: { kind: 'get-started', line: { id: 'blog-unavailable' } },
      primary: OPEN_BLOG,
      secondary: null,
    };
  }
  // The owner's own choice: a fact, never a prompt to turn it back on.
  if (!tenant.enabled) {
    return {
      chip: pickChip<BlogCopy>([writingChip, { tone: 'neutral', copy: { id: 'off' } }]),
      body: { kind: 'waiting', lines: [{ id: 'blog-off' }] },
      primary: VIEW_ARTICLES,
      secondary: null,
    };
  }

  const credits = creditsNeeded !== null;
  const drafts = articles.filter((candidate) => candidate.status === 'draft');
  const chip = pickChip<BlogCopy>([credits ? { tone: 'problem', copy: { id: 'needs-credits' } } : null, writingChip]);
  const lines: BlogCopy[] = [];
  if (credits) lines.push({ id: 'autopilot-needs-credits' });
  if (drafts.length > 0) lines.push({ id: 'drafts-to-finish', n: drafts.length });

  if (lines.length === 0 && articles.length === 0) {
    return { chip, body: WRITE_FIRST, primary: NEW_ARTICLE, secondary: null };
  }

  const lastAt = lastPublishedAt(articles);
  // A next run that cannot be read would say "next article" with no date: the Blog page's panel omits its line then.
  const nextRunAt =
    autopilot?.enabled && autopilot.nextRunAt && Number.isFinite(instant(autopilot.nextRunAt))
      ? autopilot.nextRunAt
      : null;
  const body: WebsiteCardBody<BlogCopy> =
    lines.length > 0
      ? { kind: 'waiting', lines }
      : nextRunAt
        ? { kind: 'context', line: { id: 'autopilot-next', at: nextRunAt } }
        : lastAt !== null
          ? { kind: 'context', line: { id: 'last-article', at: lastAt } }
          : WRITE_FIRST;

  const draftToContinue = latestDraft(drafts);
  const primary: WebsiteCardIntent<BlogCopy> = credits
    ? { copy: { id: 'buy-credits' }, run: 'buy-credits' }
    : draftToContinue
      ? { copy: { id: 'continue-draft' }, to: ROUTE.blogEdit(draftToContinue.id) }
      : NEW_ARTICLE;

  return { chip, body, primary, secondary: VIEW_ARTICLES };
};
